/** @jsxImportSource @emotion/react */
import 'twin.macro';

import { css } from '@emotion/react';
import { useDispatch, useSelector } from 'react-redux';
import tw from 'twin.macro';
import { RootState } from 'reducers';
import { changeSemanticSearchMethod } from 'slices/pagination.slice';
import { SemanticSearchMethod } from 'utils/enum';
import { SemanticSearchCounts } from './semanticSearchMapper';
import { countBadgeCss, SECTION_TITLE } from './SemanticSectionHeading';

const METHODS: {
  method: SemanticSearchMethod;
  label: string;
  countKey: keyof SemanticSearchCounts;
}[] = [
  { method: SemanticSearchMethod.ALL, label: 'All', countKey: 'all' },
  {
    method: SemanticSearchMethod.LEXICAL,
    label: SECTION_TITLE.exact,
    countKey: 'lexical',
  },
  {
    method: SemanticSearchMethod.SEMANTIC,
    label: SECTION_TITLE.related,
    countKey: 'semantic',
  },
];

type Props = {
  counts: SemanticSearchCounts;
};

export const SemanticSearchMethodTabs = ({ counts }: Props) => {
  const dispatch = useDispatch();
  const active = useSelector(
    (state: RootState) => state.pagination.semanticSearchMethod,
  );
  const searchTerm = useSelector(
    (state: RootState) => state.library.searchTerm as string,
  );

  return (
    <div tw="px-6 pt-4 pb-2">
      {/* Only the term truncates, so the closing quote always shows. */}
      <div
        tw="flex text-16 text-sonnant-dark whitespace-nowrap"
        title={`${counts.all.toLocaleString()} results for “${searchTerm}”`}
      >
        <span tw="flex-shrink-0 whitespace-pre">
          <span tw="font-medium">{counts.all.toLocaleString()} results</span>
          {' for “'}
        </span>
        <span tw="truncate">{searchTerm}</span>
        <span tw="flex-shrink-0">”</span>
      </div>
      <div
        tw="inline-flex items-center mt-3 p-1 rounded-full border border-[#cbccd4] bg-sonnant-grey-light"
        role="group"
      >
        {METHODS.map(({ method, label, countKey }) => {
          const isActive = active === method;
          const isDisabled =
            method !== SemanticSearchMethod.ALL && counts[countKey] === 0;
          return (
            <button
              key={method}
              type="button"
              aria-pressed={isActive}
              disabled={isDisabled}
              css={pillCss(isActive, isDisabled)}
              onClick={() => {
                if (!isActive) dispatch(changeSemanticSearchMethod(method));
              }}
            >
              {label}
              <span
                css={[
                  countBadgeCss,
                  isActive && activeBadgeCss,
                  isDisabled && tw`text-opacity-50`,
                ]}
              >
                {counts[countKey].toLocaleString()}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Segments share one track; only the active one gets a fill so the bar reads as a single-choice switcher.
const pillCss = (isActive: boolean, isDisabled: boolean) => [
  tw`inline-flex items-center gap-2 px-4 py-3 rounded-full text-14 font-medium cursor-pointer select-none transition-colors`,
  isActive
    ? tw`bg-sonnant-indigo text-white shadow-sm`
    : isDisabled
      ? [tw`bg-transparent cursor-not-allowed`, disabledResetCss]
      : tw`bg-transparent text-sonnant-dark hover:(bg-white text-sonnant-indigo)`,
];

// Overrides the global button:disabled grey (_utilities.scss) and Foundation's
// white button[disabled] text, which also applies on :hover.
const disabledResetCss = css`
  &:disabled {
    background-color: transparent !important;
    opacity: 1 !important;
    color: rgba(5, 10, 56, 0.5) !important;
  }
`;

const activeBadgeCss = css`
  ${tw`text-white`}
  background-color: rgba(255, 255, 255, 0.18);
`;
